import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { createQuoteApi } from '../../api/quoteApi';
import { mapItemMasterRowsToQuotePayload } from '../../utils/templateQuoteFromMaster';

const BHK_PREBUILT_TEMPLATES = [
  { id: 'custom', title: 'Blank / Custom Quote', desc: 'Start with an empty quote and add custom items' },
  { id: '1bhk', bhkType: '1 BHK', title: '1 BHK Standard Quotation', desc: 'Pre-filled standard 1 BHK interior items & specs' },
  { id: '2bhk', bhkType: '2 BHK', title: '2 BHK Standard Quotation', desc: 'Pre-filled standard 2 BHK interior items & specs' },
  { id: '3bhk', bhkType: '3 BHK', title: '3 BHK Standard Quotation', desc: 'Pre-filled standard 3 BHK interior items & specs' },
];

export default function DashboardQuoteModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [title, setTitle] = useState('Initial quotation');
  const [templateChoice, setTemplateChoice] = useState('custom');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLoadingProjects(true);
      setError('');
      api.get('/projects', { params: { limit: 100 } })
        .then((res) => {
          const list = res.data?.data?.projects || res.data?.projects || [];
          setProjects(list);
          if (list.length > 0 && !selectedProjectId) {
            setSelectedProjectId(list[0]._id);
          }
        })
        .catch((err) => {
          console.error(err);
          setError('Failed to load project list.');
        })
        .finally(() => setLoadingProjects(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedProjectObj = projects.find((p) => p._id === selectedProjectId);

  const filteredProjects = projects.filter((p) => {
    if (!searchFilter.trim()) return true;
    const term = searchFilter.toLowerCase();
    const name = (p.client?.name || p.clientName || '').toLowerCase();
    const phone = (p.client?.phone || p.phone || '').toLowerCase();
    const stage = (p.stage || '').toLowerCase();
    return name.includes(term) || phone.includes(term) || stage.includes(term);
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedProjectId) {
      setError('Please select a client / project for this quote.');
      return;
    }

    const clientId = typeof selectedProjectObj?.client === 'object'
      ? selectedProjectObj.client?._id
      : selectedProjectObj?.client || selectedProjectObj?._id;

    if (!clientId) {
      setError('Selected project has no valid client reference.');
      return;
    }

    setCreating(true);
    try {
      if (templateChoice === 'custom') {
        // Direct to QuoteEditor with client and project params
        onClose();
        navigate(`/quote-editor?client=${clientId}&project=${selectedProjectId}&title=${encodeURIComponent(title)}`);
        return;
      }

      // Pre-built template selected
      const tpl = BHK_PREBUILT_TEMPLATES.find((t) => t.id === templateChoice);
      const bhkParam = tpl?.bhkType || '2 BHK';

      // Fetch template rows from item master
      let items = [];
      try {
        const itemRes = await api.get('/item-master/template-rows', {
          params: { bhkType: bhkParam },
        });
        const masterRows = itemRes.data?.data?.items || itemRes.data?.items || [];
        const rawItems = mapItemMasterRowsToQuotePayload(masterRows).filter(
          (item) => String(item.section || item.space || '').trim().toLowerCase() !== 'other'
        );

        const uniqueMap = new Map();
        rawItems.forEach((item) => {
          const key = `${item.itemName || ''}|${item.section || ''}|${item.length || ''}|${item.height || ''}`;
          if (!uniqueMap.has(key)) uniqueMap.set(key, item);
        });
        items = Array.from(uniqueMap.values());
      } catch (templateErr) {
        console.warn('Could not fetch template rows from item master:', templateErr);
      }

      const res = await createQuoteApi({
        client: clientId,
        project: selectedProjectId,
        title: title.trim() || tpl?.title || 'Initial quotation',
        items,
        discount: 0,
        additionalDiscount: 0,
        isTemplateQuote: true,
        bhkType: bhkParam,
      });

      const newId = res.data?.data?.quote?._id || res.data?.quote?._id;
      onClose();
      if (newId) {
        navigate(`/quote-editor/${newId}?client=${clientId}&project=${selectedProjectId}`);
      } else {
        navigate(`/quote-editor?client=${clientId}&project=${selectedProjectId}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create quote. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal dash-action-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">📄 Create Quote</h3>
            <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: 'var(--muted)' }}>
              Generate a client proposal or quotation directly from the dashboard
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <form onSubmit={handleCreate}>
          <div className="modal-body" style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
            {error && (
              <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.65rem 0.9rem', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            {/* Select Project / Client */}
            <div className="form-group">
              <label className="form-label">Select Client / Project *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Type to filter client or phone..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{ marginBottom: 6, fontSize: '0.82rem', padding: '6px 10px' }}
              />
              <select
                className="form-select"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                required
                disabled={loadingProjects}
              >
                {loadingProjects && <option value="">Loading clients and projects...</option>}
                {!loadingProjects && filteredProjects.length === 0 && (
                  <option value="">No matching clients found</option>
                )}
                {filteredProjects.map((p) => {
                  const name = p.client?.name || p.clientName || 'Client';
                  const phone = p.client?.phone || p.phone || '';
                  const stage = p.stage || '';
                  return (
                    <option key={p._id} value={p._id}>
                      {name} {phone ? `(${phone})` : ''} — {stage}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Quote Title */}
            <div className="form-group">
              <label className="form-label">Quotation Title</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Initial quotation or 2 BHK Luxury Specs"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* Template or Custom Selection */}
            <div className="form-group">
              <label className="form-label">Select Starting Template</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
                {BHK_PREBUILT_TEMPLATES.map((t) => {
                  const isSelected = templateChoice === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setTemplateChoice(t.id)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: `1.5px solid ${isSelected ? '#976793' : '#E2E8F0'}`,
                        background: isSelected ? '#FAF5FA' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                        <strong style={{ fontSize: '0.85rem', color: isSelected ? '#2E1A46' : '#1F2937' }}>
                          {t.title}
                        </strong>
                        <span style={{ color: isSelected ? '#976793' : '#CBD5E1', fontSize: '1rem', fontWeight: 800 }}>
                          {isSelected ? '✓' : '○'}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: 'var(--muted)', lineHeight: 1.3 }}>
                        {t.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creating || !selectedProjectId}
              style={{ background: '#976793' }}
            >
              {creating ? 'Opening Editor...' : 'Proceed to Quote Editor →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
